import React, { useCallback, useContext, useMemo } from 'react';
import { RetryContext } from '@src/contexts';
import { useSelector } from 'react-redux';
import { tid } from '@utils/test';
import { autoRetrySelector, realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { RetryLayer, ErrorHolder, ErrorMessage, Wrapper, NoData } from './styled';
import useRetry from './useRetry';
import {
  RetryCard,
  ContactCard,
  DefaultLayer,
  PreparingData,
  QueryError,
  QueryDimensionErrorComp,
  ServerError,
} from './Elements';
import { checkIntervalDiff, getIntervalDiff } from './utils';
import ErrorBoundary from './ErrorBoundary';
import { useFilterContainsInvalidField } from '@src/hooks/useSetError';
const MAX_AUTO_WINDOW = 30; // in days

interface IProps {
  showError?: boolean;
  isLoading?: boolean;
  queued?: boolean;
  isEmpty?: boolean;
  fetchData?: () => void;
  horizontal?: boolean;
  LoadingLayer?: any;
  children?: any;
  mountChildAlways?: boolean;
  auto?: boolean;
  interval?: null | { from: any; to: any };
  progress?: boolean;
  showHeavyMetricMessage?: boolean;
  errorMessage?: string;
  queryDimensionError?: boolean;
  compact?: boolean; // compact mode for retry card
}

const Retry = ({
  isLoading = false, // loading state
  queued = false, // is query in queue
  showError = false, // an error occurred during loading, show retry
  isEmpty = false, // empty placeholder
  fetchData = () => null, // retry action
  horizontal = false, // render card horizontally
  LoadingLayer = DefaultLayer, // loading layer component
  children = null,
  mountChildAlways = true, // render child component irrespective of loading/error state
  auto = true, // auto retry override
  interval = null, // time interval {from, to} format, pass through context or as prop
  progress = false, // enable progress bar (uses interval info)
  showHeavyMetricMessage = false, // to identify if the retry is for metric panel
  errorMessage,
  queryDimensionError: queryError = false, // an error occurred during query execution, mostly due to invalid fields
  compact = false,
}: IProps) => {
  // auto retry feature flag
  const autoRetryEnabled = useSelector(autoRetrySelector);
  const liveModeOnTrendsSelected = useSelector(realTimeModeOnTrendsSelector);
  const queryErrorInFilter = useFilterContainsInvalidField();
  const { interval: contextInterval } = useContext(RetryContext);
  const retryInterval = interval || contextInterval;

  // allow auto-retry only if interval is less than 30 days
  // also used to show an additional message during loading for windows older than 30 days
  const autoAllowed = useMemo(
    () => checkIntervalDiff(retryInterval, MAX_AUTO_WINDOW),
    [retryInterval],
  );

  // duration for progress bar
  const progressDuration = useMemo(() => {
    return getIntervalDiff(retryInterval);
  }, [retryInterval]);

  const { retryExceeded, retryInProgress, retryRequest, handleRedirect } = useRetry({
    fetchData,
    auto: auto && autoRetryEnabled && autoAllowed,
    hasError: showError && !queryError,
    isLoading,
  });

  const renderChildren = useCallback(
    () => (mountChildAlways ? children : null),
    [mountChildAlways, children],
  );

  const loading = liveModeOnTrendsSelected ? false : isLoading;

  const showSlowMetricMessage = showHeavyMetricMessage && progressDuration > 30;

  // loading in progress
  if (loading || queued) {
    return (
      <ErrorBoundary key="loading-fragment">
        <LoadingLayer
          key="loading-layer"
          progress={progress}
          duration={progressDuration}
          showMsg={!autoAllowed}
          showHeavyMetricMessage={showSlowMetricMessage}
          queued={queued}
        />
        {renderChildren()}
      </ErrorBoundary>
    );
  }

  // auto retry delay
  if (retryInProgress) {
    return (
      <ErrorBoundary key="loading-fragment">
        {progress ? (
          <LoadingLayer
            key="loading-layer"
            progress={progress}
            duration={progressDuration}
            showMsg={!autoAllowed}
            showHeavyMetricMessage={showSlowMetricMessage}
          />
        ) : (
          <PreparingData key="preparing-layer" horizontal={horizontal} />
        )}
        {renderChildren()}
      </ErrorBoundary>
    );
  }

  if (queryError) {
    return <QueryDimensionErrorComp />;
  }

  if (queryErrorInFilter) {
    return (
      <Wrapper compact={compact}>
        <RetryLayer {...tid('retry-layer')}>
          <ErrorHolder>
            <ErrorMessage horizontal={horizontal}>
              <QueryError />
            </ErrorMessage>
          </ErrorHolder>
        </RetryLayer>
      </Wrapper>
    );
  }

  if (errorMessage) {
    return (
      <Wrapper compact={compact}>
        <RetryLayer {...tid('retry-layer')}>
          <ErrorHolder>
            <ErrorMessage horizontal={horizontal}>
              <ServerError message={errorMessage} />
            </ErrorMessage>
          </ErrorHolder>
        </RetryLayer>
        {renderChildren()}
      </Wrapper>
    );
  }

  // there are errors
  if (showError) {
    return (
      <Wrapper compact={compact}>
        <RetryLayer {...tid('retry-layer')}>
          <ErrorHolder>
            <ErrorMessage horizontal={horizontal}>
              {retryExceeded ? (
                <ContactCard onClick={handleRedirect} horizontal={horizontal} />
              ) : (
                <RetryCard
                  onClick={retryRequest}
                  horizontal={horizontal}
                  showHeavyMetricMessage={showSlowMetricMessage}
                />
              )}
            </ErrorMessage>
          </ErrorHolder>
        </RetryLayer>
        {renderChildren()}
      </Wrapper>
    );
  }

  // no data available
  if (isEmpty) {
    return (
      <Wrapper compact={compact}>
        <NoData />
      </Wrapper>
    );
  }

  // loading is done, there are no errors
  return <ErrorBoundary key="loading-fragment">{children}</ErrorBoundary>;
};

export default React.memo(Retry);
